import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Truck, FileText, XCircle, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';

var API = process.env.REACT_APP_BACKEND_URL + '/api';

function EwayBillDialog(props) {
  var open = props.open;
  var onOpenChange = props.onOpenChange;
  var invoice = props.invoice;
  var onSuccess = props.onSuccess;
  var autoPrompt = props.autoPrompt;

  var loadingState = React.useState(false);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var ewayBillState = React.useState(null);
  var ewayBill = ewayBillState[0];
  var setEwayBill = ewayBillState[1];

  var fetchingState = React.useState(false);
  var fetching = fetchingState[0];
  var setFetching = fetchingState[1];

  var cancellingState = React.useState(false);
  var cancelling = cancellingState[0];
  var setCancelling = cancellingState[1];

  var cancelReasonState = React.useState('');
  var cancelReason = cancelReasonState[0];
  var setCancelReason = cancelReasonState[1];

  var showCancelState = React.useState(false);
  var showCancel = showCancelState[0];
  var setShowCancel = showCancelState[1];

  var formState = React.useState({
    transporter_name: '',
    vehicle_number: '',
    transport_mode: 'road'
  });
  var form = formState[0];
  var setForm = formState[1];

  React.useEffect(function() {
    if (open && invoice && invoice.id) {
      fetchEwayBill();
    }
  }, [open, invoice]);

  function fetchEwayBill() {
    if (!invoice || !invoice.id) return;
    setFetching(true);
    var token = localStorage.getItem('token');
    axios.get(API + '/invoices/' + invoice.id + '/eway-bill', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(res) {
      if (res.data && res.data.id) {
        setEwayBill(res.data);
      } else {
        setEwayBill(null);
      }
      setFetching(false);
    }).catch(function() {
      setEwayBill(null);
      setFetching(false);
    });
  }

  function handleGenerate(e) {
    e.preventDefault();
    if (!form.transporter_name.trim()) {
      toast.error('Transporter name is required');
      return;
    }
    setLoading(true);
    var token = localStorage.getItem('token');
    axios.post(API + '/eway-bills/generate', {
      invoice_id: invoice.id,
      transporter_name: form.transporter_name,
      vehicle_number: form.vehicle_number,
      transport_mode: form.transport_mode
    }, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(res) {
      toast.success('E-way Bill ' + res.data.eway_bill_number + ' generated!');
      setEwayBill(res.data);
      fetchEwayBill();
      setLoading(false);
      if (onSuccess) onSuccess(res.data);
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Failed to generate E-way Bill');
      setLoading(false);
    });
  }

  function handleCancel() {
    if (!cancelReason.trim()) {
      toast.error('Cancel reason is required');
      return;
    }
    setCancelling(true);
    var token = localStorage.getItem('token');
    axios.post(API + '/eway-bills/' + ewayBill.id + '/cancel', {
      reason: cancelReason
    }, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function() {
      toast.success('E-way Bill cancelled');
      setEwayBill(null);
      setShowCancel(false);
      setCancelReason('');
      setCancelling(false);
      fetchEwayBill();
      if (onSuccess) onSuccess();
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
      setCancelling(false);
    });
  }

  function formatCurrency(val) {
    return '\u20B9' + (val || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
  }

  function renderGenerateForm() {
    return React.createElement('form', { onSubmit: handleGenerate, className: 'space-y-4' },
      autoPrompt && React.createElement('div', {
        className: 'flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg',
        'data-testid': 'eway-bill-auto-prompt'
      },
        React.createElement(AlertTriangle, { className: 'w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0' }),
        React.createElement('div', null,
          React.createElement('p', { className: 'font-semibold text-amber-800' }, 'E-way Bill Required'),
          React.createElement('p', { className: 'text-sm text-amber-700' },
            'Invoice value ', formatCurrency(invoice?.grand_total), ' exceeds \u20B950,000. E-way Bill is mandatory for goods transport.'
          )
        )
      ),

      React.createElement('div', { className: 'grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg' },
        React.createElement('div', null,
          React.createElement('p', { className: 'text-xs text-slate-500' }, 'Invoice'),
          React.createElement('p', { className: 'font-semibold' }, invoice?.invoice_number)
        ),
        React.createElement('div', null,
          React.createElement('p', { className: 'text-xs text-slate-500' }, 'Total Value'),
          React.createElement('p', { className: 'font-semibold text-green-600' }, formatCurrency(invoice?.grand_total))
        ),
        React.createElement('div', null,
          React.createElement('p', { className: 'text-xs text-slate-500' }, 'Buyer'),
          React.createElement('p', { className: 'font-medium' }, invoice?.customer_name)
        ),
        React.createElement('div', null,
          React.createElement('p', { className: 'text-xs text-slate-500' }, 'GSTIN'),
          React.createElement('p', { className: 'font-mono text-sm' }, invoice?.customer_gstin || 'N/A')
        )
      ),

      React.createElement('div', { className: 'space-y-3' },
        React.createElement('div', { className: 'space-y-1' },
          React.createElement(Label, { htmlFor: 'transporter_name' }, 'Transporter Name *'),
          React.createElement(Input, {
            id: 'transporter_name',
            value: form.transporter_name,
            onChange: function(e) { setForm(Object.assign({}, form, { transporter_name: e.target.value })); },
            placeholder: 'Enter transporter name',
            'data-testid': 'eway-transporter-name'
          })
        ),
        React.createElement('div', { className: 'space-y-1' },
          React.createElement(Label, { htmlFor: 'vehicle_number' }, 'Vehicle Number'),
          React.createElement(Input, {
            id: 'vehicle_number',
            value: form.vehicle_number,
            onChange: function(e) { setForm(Object.assign({}, form, { vehicle_number: e.target.value.toUpperCase() })); },
            placeholder: 'e.g., KA01AB1234',
            'data-testid': 'eway-vehicle-number'
          })
        ),
        React.createElement('div', { className: 'space-y-1' },
          React.createElement(Label, null, 'Transport Mode'),
          React.createElement(Select, {
            value: form.transport_mode,
            onValueChange: function(val) { setForm(Object.assign({}, form, { transport_mode: val })); }
          },
            React.createElement(SelectTrigger, { 'data-testid': 'eway-transport-mode' },
              React.createElement(SelectValue, { placeholder: 'Select mode' })
            ),
            React.createElement(SelectContent, null,
              React.createElement(SelectItem, { value: 'road' }, 'Road'),
              React.createElement(SelectItem, { value: 'rail' }, 'Rail'),
              React.createElement(SelectItem, { value: 'air' }, 'Air'),
              React.createElement(SelectItem, { value: 'ship' }, 'Ship')
            )
          )
        )
      ),

      React.createElement('div', {
        className: 'flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700'
      },
        React.createElement(FileText, { className: 'w-4 h-4 flex-shrink-0' }),
        'NIC E-way Bill API integration pending. This will generate a mock E-way Bill number.'
      ),

      React.createElement('div', { className: 'flex justify-end gap-2 pt-2 border-t' },
        React.createElement(Button, {
          type: 'button',
          variant: 'outline',
          onClick: function() { onOpenChange(false); }
        }, 'Cancel'),
        React.createElement(Button, {
          type: 'submit',
          disabled: loading,
          className: 'gap-2 bg-green-600 hover:bg-green-700',
          'data-testid': 'generate-eway-bill-btn'
        },
          loading ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }) : React.createElement(Truck, { className: 'w-4 h-4' }),
          'Generate E-way Bill'
        )
      )
    );
  }

  function renderEwayBillDetails() {
    if (!ewayBill) return null;
    var isActive = ewayBill.status === 'active';

    return React.createElement('div', { className: 'space-y-4' },
      React.createElement('div', { className: 'flex items-center justify-between p-4 rounded-lg ' + (isActive ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200') },
        React.createElement('div', { className: 'flex items-center gap-3' },
          isActive
            ? React.createElement(CheckCircle2, { className: 'w-6 h-6 text-green-600' })
            : React.createElement(XCircle, { className: 'w-6 h-6 text-red-600' }),
          React.createElement('div', null,
            React.createElement('p', { className: 'font-bold text-lg', 'data-testid': 'eway-bill-number' }, ewayBill.eway_bill_number),
            React.createElement('p', { className: 'text-sm ' + (isActive ? 'text-green-700' : 'text-red-700') },
              isActive ? 'Active' : 'Cancelled'
            )
          )
        ),
        React.createElement(Badge, {
          className: isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }, isActive ? 'ACTIVE' : 'CANCELLED')
      ),

      ewayBill.is_mock && React.createElement('div', {
        className: 'flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700'
      },
        React.createElement(AlertTriangle, { className: 'w-4 h-4' }),
        'MOCK E-way Bill - NIC API credentials pending'
      ),

      React.createElement('div', { className: 'grid grid-cols-2 gap-4 text-sm' },
        React.createElement('div', null,
          React.createElement('p', { className: 'text-slate-500' }, 'Invoice'),
          React.createElement('p', { className: 'font-medium' }, ewayBill.invoice_number)
        ),
        React.createElement('div', null,
          React.createElement('p', { className: 'text-slate-500' }, 'Total Value'),
          React.createElement('p', { className: 'font-medium' }, formatCurrency(ewayBill.total_value))
        ),
        React.createElement('div', null,
          React.createElement('p', { className: 'text-slate-500' }, 'Transporter'),
          React.createElement('p', { className: 'font-medium' }, ewayBill.transporter_name)
        ),
        React.createElement('div', null,
          React.createElement('p', { className: 'text-slate-500' }, 'Vehicle'),
          React.createElement('p', { className: 'font-medium' }, ewayBill.vehicle_number || 'N/A')
        ),
        React.createElement('div', null,
          React.createElement('p', { className: 'text-slate-500' }, 'Transport Mode'),
          React.createElement('p', { className: 'font-medium capitalize' }, ewayBill.transport_mode)
        ),
        React.createElement('div', null,
          React.createElement('p', { className: 'text-slate-500' }, 'Generated'),
          React.createElement('p', { className: 'font-medium' }, new Date(ewayBill.generated_at).toLocaleString())
        ),
        React.createElement('div', null,
          React.createElement('p', { className: 'text-slate-500' }, 'Valid Upto'),
          React.createElement('p', { className: 'font-medium' }, new Date(ewayBill.valid_upto).toLocaleString())
        ),
        React.createElement('div', null,
          React.createElement('p', { className: 'text-slate-500' }, 'Generated By'),
          React.createElement('p', { className: 'font-medium' }, ewayBill.generated_by_name)
        )
      ),

      ewayBill.status === 'cancelled' && React.createElement('div', { className: 'p-3 bg-red-50 border border-red-200 rounded-lg text-sm' },
        React.createElement('p', { className: 'font-semibold text-red-800' }, 'Cancellation Reason:'),
        React.createElement('p', { className: 'text-red-700' }, ewayBill.cancel_reason)
      ),

      isActive && !showCancel && React.createElement('div', { className: 'flex justify-end gap-2 pt-2 border-t' },
        React.createElement(Button, {
          variant: 'outline',
          onClick: function() { onOpenChange(false); }
        }, 'Close'),
        React.createElement(Button, {
          variant: 'outline',
          className: 'text-red-600 border-red-300 hover:bg-red-50',
          onClick: function() { setShowCancel(true); },
          'data-testid': 'cancel-eway-bill-btn'
        },
          React.createElement(XCircle, { className: 'w-4 h-4 mr-1' }),
          'Cancel E-way Bill'
        )
      ),

      isActive && showCancel && React.createElement('div', { className: 'space-y-3 pt-2 border-t' },
        React.createElement(Label, null, 'Cancel Reason *'),
        React.createElement(Input, {
          value: cancelReason,
          onChange: function(e) { setCancelReason(e.target.value); },
          placeholder: 'Enter cancellation reason',
          'data-testid': 'eway-cancel-reason'
        }),
        React.createElement('div', { className: 'flex justify-end gap-2' },
          React.createElement(Button, {
            variant: 'outline',
            onClick: function() { setShowCancel(false); setCancelReason(''); }
          }, 'Back'),
          React.createElement(Button, {
            onClick: handleCancel,
            disabled: cancelling,
            className: 'bg-red-600 hover:bg-red-700',
            'data-testid': 'confirm-cancel-eway-btn'
          },
            cancelling ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin mr-1' }) : null,
            'Confirm Cancel'
          )
        )
      ),

      !isActive && React.createElement('div', { className: 'flex justify-end pt-2 border-t' },
        React.createElement(Button, {
          variant: 'outline',
          onClick: function() { onOpenChange(false); }
        }, 'Close')
      )
    );
  }

  return React.createElement(Dialog, { open: open, onOpenChange: onOpenChange },
    React.createElement(DialogContent, { className: 'max-w-lg max-h-[90vh] overflow-y-auto' },
      React.createElement(DialogHeader, null,
        React.createElement(DialogTitle, { className: 'flex items-center gap-2' },
          React.createElement(Truck, { className: 'w-5 h-5' }),
          'E-way Bill',
          ewayBill && ewayBill.eway_bill_number ? ' - ' + ewayBill.eway_bill_number : ''
        )
      ),
      fetching
        ? React.createElement('div', { className: 'flex items-center justify-center py-8' },
            React.createElement(Loader2, { className: 'w-6 h-6 animate-spin text-slate-400' })
          )
        : ewayBill && ewayBill.id
          ? renderEwayBillDetails()
          : renderGenerateForm()
    )
  );
}

export default EwayBillDialog;
